import { Component } from '@theme/component';
import { CartLinesUpdateEvent } from '@shopify/events';

const ADDED_FEEDBACK_DURATION = 2000;

/**
 * "Complete your routine" — the product on the page plus what it is usually bought with,
 * added to the cart in one request.
 *
 * Everything visible is server-rendered, so with JS off the card still reads as an honest
 * list of what pairs with this product; only the ticking and the one-click add go quiet.
 *
 * @typedef {object} RoutineBundleRefs
 * @property {HTMLButtonElement} button - The add button.
 * @property {HTMLElement} total - Where the running total is printed.
 * @property {HTMLElement} [currentPrice] - Price cell for the product on the page.
 *
 * @extends {Component<RoutineBundleRefs>}
 */
class RoutineBundle extends Component {
  requiredRefs = ['button', 'total'];

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #feedbackTimeout;

  connectedCallback() {
    super.connectedCallback?.();

    this.addEventListener('change', this.#handleChange);

    // Only five products in the catalogue have variants, but on those the price shown here
    // has to follow the picker rather than sit on whichever variant rendered first.
    document.addEventListener('shopify:product:select', this.#onVariantSelect);
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    document.removeEventListener('shopify:product:select', this.#onVariantSelect);
    if (this.#feedbackTimeout) clearTimeout(this.#feedbackTimeout);
  }

  /** @returns {HTMLInputElement[]} Every checkbox in the card. */
  get #checkboxes() {
    return /** @type {HTMLInputElement[]} */ ([...this.querySelectorAll('.routine-bundle__check')]);
  }

  /** @param {Event} event - A change from one of the checkboxes. */
  #handleChange = (event) => {
    if (!(event.target instanceof HTMLInputElement)) return;
    this.#render();
  };

  /**
   * Keeps the page's own variant picker and this card in step.
   * @param {Event} event - The ProductSelectEvent from the variant picker.
   */
  #onVariantSelect = async (event) => {
    const line = this.querySelector('[data-current-product]');
    if (!(line instanceof HTMLInputElement)) return;

    try {
      const { variant } = await /** @type {any} */ (event).promise;
      if (!variant) return;

      line.dataset.variantId = String(variant.id).split('/').pop() ?? line.dataset.variantId;

      // Prices arrive as a decimal string; the rest of the card counts in cents.
      const cents = Math.round(Number(variant.price?.amount ?? 0) * 100);
      if (Number.isFinite(cents) && cents > 0) {
        line.dataset.price = String(cents);
        const { currentPrice } = this.refs;
        if (currentPrice) currentPrice.textContent = this.#money(cents);
      }

      line.disabled = variant.availableForSale === false;
      if (line.disabled) line.checked = false;

      this.#render();
    } catch {
      // A rejected variant swap is the picker's problem to report, not this card's.
    }
  };

  /** Repaints the total and the button's enabled state from the current ticks. */
  #render() {
    const chosen = this.#checkboxes.filter((box) => box.checked);
    const total = chosen.reduce((sum, box) => sum + Number(box.dataset.price ?? 0), 0);

    this.refs.total.textContent = this.#money(total);

    // Catalog mode's disable is permanent for the life of the page. Only the "nothing is
    // ticked" case is ours to toggle.
    if (!this.refs.button.hasAttribute('data-catalog-locked')) {
      this.refs.button.disabled = chosen.length === 0;
    }
  }

  /**
   * @param {number} cents - An amount in the shop's minor units.
   * @returns {string} The amount in the shop's money format.
   */
  #money(cents) {
    const currency = this.dataset.currency || 'CAD';

    return new Intl.NumberFormat(document.documentElement.lang || 'en-CA', {
      style: 'currency',
      currency,
      currencyDisplay: 'narrowSymbol',
    }).format(cents / 100);
  }

  /**
   * Adds every ticked line in one request, then hands Horizon the same event a product form
   * would, so the drawer, the cart bubble and the gift ladder all refresh as usual.
   *
   * A line already in the cart has its quantity increased rather than replaced — that is
   * what /cart/add.js does, and it is the honest reading of "add this to my cart".
   */
  async addBundle() {
    const { button } = this.refs;
    if (button.disabled || button.hasAttribute('data-catalog-locked')) return;

    const lines = this.#checkboxes
      .filter((box) => box.checked)
      .map((box) => box.dataset.variantId)
      .filter(Boolean);

    if (lines.length === 0) return;

    button.disabled = true;

    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : null))
      .filter(Boolean);

    const deferred = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: lines.map((id) => ({ merchandiseId: String(id), quantity: 1 })),
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: lines.map((id) => ({ id: Number(id), quantity: 1 })),
          sections: sectionIds.join(','),
        }),
      }).then((res) => res.json());

      const cart = await this.#refreshCart();

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          didError: Boolean(response.status),
          items: cart.items,
          source: 'routine-bundle',
          sourceId: this.dataset.sectionId ?? '',
          itemCount: lines.length,
        },
      });

      this.#showFeedback(response.status ? response.message : null);
    } catch (error) {
      deferred.reject(error);
      this.#showFeedback(null);
    }
  }

  /**
   * Confirms briefly, then puts the button back.
   * @param {string | null} errorMessage - An error to surface, if the add failed.
   */
  #showFeedback(errorMessage) {
    const { button } = this.refs;
    const original = button.dataset.originalLabel ?? button.textContent ?? '';
    button.dataset.originalLabel = original;
    button.textContent = errorMessage ?? Theme.translations?.added ?? 'Added';

    if (this.#feedbackTimeout) clearTimeout(this.#feedbackTimeout);
    this.#feedbackTimeout = setTimeout(() => {
      button.textContent = original;
      if (!button.hasAttribute('data-catalog-locked')) this.#render();
    }, ADDED_FEEDBACK_DURATION);
  }

  /**
   * Reads the current cart, preferring the live cart component when one is on the page.
   * @returns {Promise<any>} The cart payload.
   */
  async #refreshCart() {
    const cartItemsComponent = document.querySelector('cart-items-component');

    if (cartItemsComponent) {
      await customElements.whenDefined('cart-items-component');
      // @ts-expect-error - fetchCartData is defined on CartItemsComponent
      return cartItemsComponent.fetchCartData();
    }

    return fetch(`${Theme.routes.cart_url}.json`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    }).then((response) => {
      if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
      return response.json();
    });
  }
}

if (!customElements.get('routine-bundle')) {
  customElements.define('routine-bundle', RoutineBundle);
}
